import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  MapPin,
  CheckCircle,
  XCircle,
  Clock,
  Building2
} from 'lucide-react';
import Swal from 'sweetalert2';
import RouteModal from '../components/RouteModal';
import RouteCityModal from '../components/RouteCityModal';
import { routeApi } from '../services/api';

const RouteManagement = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('true');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState('');
  const [showCityModal, setShowCityModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);

  // Helper function to convert minutes to hours and minutes
  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hr`;
    }
    return `${hours} hr ${remainingMinutes} min`;
  };

  // Helper function to format total duration for statistics
  const formatTotalDuration = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) {
      return `${minutes} min`;
    }
    if (minutes === 0) {
      return `${hours} hr`;
    }
    return `${hours} hr ${minutes} min`;
  };

  // Load routes from API
  const loadRoutes = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Try the regular routes API first (most likely to work)
      let response;
      try {
        response = await routeApi.getRoutes();
      } catch (regularError) {
        // Try with cities API as fallback
        try {
          response = await routeApi.getRoutesWithCities();
        } catch (cityError) {
          throw regularError; // Throw the original error
        }
      }
      
      // Handle different response structures
      let routes = [];
      if (response?.value && Array.isArray(response.value)) {
        routes = response.value;
      } else if (Array.isArray(response)) {
        routes = response;
      } else if (response && typeof response === 'object') {
        routes = [response];
      } else {
        routes = [];
      }
      
      const activeRoutes = routes.filter(route => route.isActive);
      
      setRoutes(activeRoutes);
      setTotalCount(activeRoutes.length);
      setTotalPages(1);
      
      if (activeRoutes.length === 0 && routes.length === 0) {
        setError('No routes found in the database. Please add some routes first.');
      }
      
    } catch (err) {
      console.error('Error loading routes:', err);
      
      let errorMessage = 'Failed to load routes. ';
      if (err.message.includes('Network Error')) {
        errorMessage += 'Check if the backend server is running.';
      } else if (err.message.includes('404')) {
        errorMessage += 'API endpoint not found.';
      } else if (err.message.includes('500')) {
        errorMessage += 'Server error occurred.';
      } else {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  // Load all routes (including inactive) when needed
  const loadAllRoutes = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Try with cities first, fallback to regular route API
      let response;
      try {
        response = await routeApi.getRoutesWithCities();
      } catch (cityError) {
        response = await routeApi.getRoutes();
      }
      
      // Handle different response structures
      let routes = [];
      if (response?.value && Array.isArray(response.value)) {
        routes = response.value;
      } else if (Array.isArray(response)) {
        routes = response;
      } else if (response && typeof response === 'object') {
        routes = [response];
      }
      
      setRoutes(routes);
      setTotalCount(routes.length);
      setTotalPages(1);
    } catch (err) {
      console.error('Error loading all routes:', err);
      setError('Failed to load routes. Please try again.');
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load - get all routes
  useEffect(() => {
    loadRoutes();
  }, []);

  // Apply frontend filtering for display only
  const filteredRoutes = routes.filter(route => {
    // Only apply search filter if there's a search term
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      const fromCityName = route.fromCityName || route.fromLocation || '';
      const toCityName = route.toCityName || route.toLocation || '';
      const matchesSearch = fromCityName.toLowerCase().includes(searchLower) ||
                           toCityName.toLowerCase().includes(searchLower) ||
                           route.description?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    
    // Only apply status filter if there's a status selected
    if (filterStatus && filterStatus !== '') {
      const isActive = filterStatus === 'true';
      if (route.isActive !== isActive) return false;
    }
    
    return true;
  });

  // Handle status filter change
  const handleStatusFilterChange = async (status) => {
    setFilterStatus(status);
    
    if (status === 'true') {
      // Show only active routes
      await loadRoutes();
    } else if (status === 'false') {
      // Show inactive routes - need to load all routes first
      await loadAllRoutes();
    } else if (status === '') {
      // Show all routes (including both active and inactive)
      await loadAllRoutes();
    }
  };

  const handleAddRoute = () => {
    setEditingRoute(null);
    setShowModal(true);
  };

  const handleEditRoute = (route) => {
    setEditingRoute(route);
    setShowModal(true);
  };

  // Handle manage cities for route
  const handleManageCities = (route) => {
    setSelectedRoute(route);
    setShowCityModal(true);
  };

  const handleToggleStatus = async (id) => {
    const route = routes.find(r => r.routeID === id);
    if (!route) return;

    const newStatus = route.isActive ? 'inactive' : 'active';
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to mark this route as ${newStatus}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, mark as ${newStatus}`,
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await routeApi.toggleStatus(id);
        // Reload routes after successful toggle
        await loadRoutes();
        
        Swal.fire(
          'Success!',
          `Route has been marked as ${newStatus} successfully.`,
          'success'
        );
      } catch (err) {
        console.error('Error toggling status:', err);
        Swal.fire(
          'Error!',
          `Failed to update status: ${err.message}`,
          'error'
        );
      }
    }
  };

  const handleSaveRoute = async (routeData) => {
    try {
      if (editingRoute) {
        // Update existing route
        await routeApi.updateRoute(editingRoute.routeID, routeData);
        Swal.fire(
          'Updated!',
          'Route has been updated successfully.',
          'success'
        );
      } else {
        // Create new route
        await routeApi.createRoute(routeData);
        Swal.fire(
          'Created!',
          'Route has been created successfully.',
          'success'
        );
      }
      
      // Reload routes after successful save
      await loadRoutes();
      setShowModal(false);
      setEditingRoute(null);
    } catch (err) {
      console.error('Error saving route:', err);
      throw err; // Re-throw to let the modal handle the error
    }
  };

  // Remove individual page loading since we have global loading
  // if (loading && routes.length === 0) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen">
  //       <BusLoading size="large" />
  //     </div>
  //   );
  // }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Route Management</h1>
              <p className="text-gray-600">Manage your bus routes and destinations</p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                onClick={handleAddRoute}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Route
              </button>
            </div>
          </div>
        </div>
          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              <strong>Error:</strong> {error}
              <button 
                onClick={() => setError('')}
                className="float-right font-bold text-red-700 hover:text-red-900"
              >
                ×
              </button>
            </div>
          )}


        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search routes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
                <option value="">All</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('');
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Filter Results */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredRoutes.length} of {totalCount} routes
          </div>
        </div>

        {/* Routes Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Route Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRoutes.map((route) => (
                  <tr key={route.routeID} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <MapPin className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {(route.fromCityName || route.fromLocation) || 'Unknown'} → {(route.toCityName || route.toLocation) || 'Unknown'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {route.distance || 0} km • {formatDuration(route.estimatedDuration || 0)} • Rs {(route.basePrice || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        route.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {route.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleManageCities(route)}
                          className="text-purple-600 hover:text-purple-700 p-2 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Manage Cities"
                        >
                          <Building2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditRoute(route)}
                          className="text-blue-600 hover:text-blue-700 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit Route"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(route.routeID)}
                          className={`p-2 rounded-lg transition-colors ${
                            route.isActive 
                              ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50' 
                              : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                          }`}
                          title={route.isActive ? 'Mark as Inactive' : 'Mark as Active'}
                        >
                          {route.isActive ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>

          {(!filteredRoutes || filteredRoutes.length === 0) && !loading && (
            <div className="text-center py-12">
              <MapPin className="mx-auto h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {(!routes || routes.length === 0) ? 'No routes in database' : 'No routes match your filters'}
              </h3>
              <p className="text-gray-500">
                {(!routes || routes.length === 0)
                  ? 'Add your first route to get started.' 
                  : 'Try adjusting your search criteria or clear the filters to see all routes.'}
              </p>
              {error && (
                <div className="mt-4 text-sm text-red-600">
                  Error: {error}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Route Modal */}
      <RouteModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingRoute(null);
        }}
        onSave={handleSaveRoute}
        route={editingRoute}
      />

      {/* Route City Modal */}
      <RouteCityModal
        isOpen={showCityModal}
        onClose={() => {
          setShowCityModal(false);
          setSelectedRoute(null);
        }}
        routeId={selectedRoute?.routeID}
        routeName={selectedRoute ? `${selectedRoute.fromLocation} → ${selectedRoute.toLocation}` : ''}
      />
    </div>
  );
};

export default RouteManagement;
